import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useGetPartnersQuery } from '../../../reactWrapper/Redux/apiSlice';

import Table from '../Table';
import Pagination from '../../Pagination/Pagination';
import Status from '../../Status/Status';

const PAGE_SIZE = 10;

/* Tab key -> label. The keys are what the API's ?status= takes. */
const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'verified', label: 'Verified' },
  { key: 'suspended', label: 'Suspended' },
  /* Verified partners waiting for a new name to be approved. */
  { key: 'name_request', label: 'Name changes' },
];

const inputClass =
  'h-10 w-64 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
};

const capitalise = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

const PartnersTable = () => {
  const history = useHistory();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  /* Wait for the admin to stop typing before asking the API. */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isFetching, isError, refetch } = useGetPartnersQuery({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    status,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;
  const counts = data?.counts;

  const tableHeader = useMemo(
    () => [
      { title: 'Partner', key: 'partner' },
      { title: 'Contact', key: 'contact' },
      { title: 'Status', key: 'status' },
      { title: 'Code', key: 'code' },
      { title: 'Talents', key: 'talents' },
      { title: 'Clients', key: 'clients' },
      { title: 'Joined', key: 'joined' },
      { title: '', key: 'details' },
    ],
    []
  );

  const tableBody = useMemo(() => {
    return rows.map((each) => [
      <div className='flex items-center gap-[18px]'>
        <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-navy text-sm font-semibold text-white'>
          {getInitials(each.name)}
        </div>
        <div className='min-w-0'>
          <p className='truncate font-semibold text-brand-navy'>{each.name}</p>
          <p className='text-xs text-muted-foreground'>{each.account_type === 'individual' ? 'Individual' : 'Organization'}</p>
          {each.pending_name && (
            <p className='mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800'>
              Wants to be "{each.pending_name}"
            </p>
          )}
        </div>
      </div>,
      <div className='min-w-0'>
        <p className='truncate text-sm text-foreground'>{each.contact_name || '—'}</p>
        <p className='truncate text-xs text-muted-foreground'>{each.email}</p>
        <p className='text-xs text-muted-foreground'>{each.phone || ''}</p>
      </div>,
      <Status label={capitalise(each.status)} />,
      <span className='font-mono text-xs text-muted-foreground'>{each.current_code || '—'}</span>,
      <span className='tabular-nums'>{each.summary?.talents ?? 0}</span>,
      <span className='tabular-nums'>{each.summary?.clients ?? 0}</span>,
      new Date(each.created_at).toLocaleDateString(),
      <Link
        className='inline-block rounded-lg border border-border px-[11.7px] py-[1.56px] text-sm text-muted-foreground'
        to={`/partners/${each.id}`}
      >
        {each.status === 'pending' || each.pending_name ? 'Review' : 'Details'}
      </Link>,
    ]);
  }, [rows]);

  const filtered = status !== 'all' || debouncedSearch;

  return (
    <div className='w-full space-y-4'>
      <div role='tablist' className='flex flex-wrap gap-1 border-b border-border'>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            role='tab'
            aria-selected={status === tab.key}
            onClick={() => {
              setStatus(tab.key);
              setPage(1);
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              status === tab.key
                ? 'border-brand-navy font-semibold text-brand-navy'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {counts ? (
              <span className='ml-1.5 rounded-full bg-surface px-1.5 py-0.5 font-mono text-[11px] font-normal text-muted-foreground'>
                {counts[tab.key] ?? 0}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className='flex flex-col gap-1.5'>
        <label htmlFor='partner-search' className='text-xs font-semibold text-muted-foreground'>
          Search
        </label>
        <input
          id='partner-search'
          type='search'
          placeholder='Name, contact, email or code'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={inputClass}
        />
      </div>

      {isLoading ? (
        <div className='flex h-48 items-center justify-center'>
          <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-brand-navy' />
        </div>
      ) : isError ? (
        <div className='flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-card py-16'>
          <p className='font-medium text-muted-foreground'>Partners couldn't be loaded.</p>
          <button
            type='button'
            onClick={refetch}
            className='mt-4 rounded-lg border border-border px-4 py-2 text-sm text-brand-navy hover:bg-surface'
          >
            Try again
          </button>
        </div>
      ) : rows.length ? (
        <div className={isFetching ? 'opacity-60 transition-opacity' : ''}>
          {/* Clicking anywhere on a row opens that partner. */}
          <Table
            header={tableHeader}
            label=''
            column={tableBody}
            onRowClick={(index) => history.push(`/partners/${rows[index].id}`)}
          />
          <section className='w-full py-5'>
            <Pagination
              rangeStart={total ? (page - 1) * PAGE_SIZE + 1 : 0}
              totalCount={total}
              currentPage={page}
              pageSize={PAGE_SIZE}
              pageColumn={1}
              siblingCount={1}
              onPageChange={(p) => setPage(p)}
            />
          </section>
        </div>
      ) : (
        <div className='flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-card py-16'>
          <p className='font-medium text-muted-foreground'>
            {filtered ? 'No partners match this filter.' : 'No partners have registered yet.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PartnersTable;
